import React from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';

import { MyStore } from '../hooks/useMyStore';

interface Props {
  store: MyStore | null;
  onNavigate: (page: string) => void;
  portal: 'manufacturer' | 'distributor';
  /** Hide the banner on the subscription page itself so it doesn't double-up. */
  currentPage: string;
}

/**
 * Shown across every admin page when the store's `isSubscriptionActive`
 * flag is false (or null). The admin can keep using the app — this is
 * just a nag with a one-tap shortcut to renew.
 *
 * Mobile FSRs get a hard gate instead (see `SubscriptionExpiredScreen`).
 */
export const SubscriptionExpiredBanner: React.FC<Props> = ({
  store,
  onNavigate,
  portal,
  currentPage,
}) => {
  if (!store) return null;
  if (store.isSubscriptionActive) return null;
  // Free accounts (master-admin override) don't have a subscription to
  // renew — showing this banner would just confuse the tenant admin.
  if (store.isFreeAccount) return null;
  // Don't double-up on the subscription pages — the page itself surfaces
  // the same call-to-action.
  if (
    currentPage === 'mfr-subscription' ||
    currentPage === 'dist-subscription' ||
    currentPage === 'setup-manage' ||
    currentPage === 'subscription-verification'
  ) {
    return null;
  }

  const target = portal === 'manufacturer' ? 'mfr-subscription' : 'dist-subscription';

  return (
    <div className="px-6 pt-4">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
          <AlertTriangle size={16} className="text-amber-600" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900">
            Your subscription has expired
          </div>
          <div className="text-xs text-amber-800/80">
            Renew now to keep field reps working without interruption. The dashboard stays usable
            in the meantime.
          </div>
        </div>
        <button
          onClick={() => onNavigate(target)}
          className="h-9 px-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold inline-flex items-center gap-1 shrink-0"
        >
          Renew now
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};
